import React, { useContext } from 'react';
import { useTPAComponentsContext } from 'wix-ui-tpa/cssVars';
import { faqButtonClickSrc63Evid836 } from '@wix/bi-logger-labs-data/v2';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import type { Category } from '@wix/ambassador-faq-category-v2-category/types';
import VerticalTabs from './VerticalCategories';
import HorizontalCategories from './HorizontalCategories';
import { useCategories } from '../../../../hooks/useCategories';
import { AppContext } from '../SettingsProvider/SettingsProvider';
import { TabItem } from 'wix-ui-tpa';
import { LAST_CATEGORY_ID_FOR_MIGRATION } from '../../../../constants/migration';
import { getCategoryTitleForOOI } from '../../../../utils/getCategoryTitleForOOI';

export interface TabsWrapperProps {
  validLists: Category[];
}

const TabsWrapper = ({ validLists }: TabsWrapperProps) => {
  let isMigratingCategory = false;
  const logger = useBi();
  const { appDefinitionId } = useEnvironment();
  const { setFirstQuestionId, searchTerm, fetchError, baseURL } =
    useContext(AppContext);
  const { rtl, seo } = useTPAComponentsContext();
  const { t } = useTranslation();

  const {
    categoriesToRender,
    activeTab,
    setActiveTab,
    splitIntoCategories,
    topCategoriesPosition,
    sideCategoriesPosition,
  } = useCategories(validLists);
  const categoriesIds = categoriesToRender.map(({ id }) => id);
  const isAnyCategoryChanged = categoriesToRender.some(
    ({ updatedDate, createdDate }) =>
      createdDate &&
      updatedDate &&
      Math.abs(+new Date(updatedDate) - +new Date(createdDate)) / 36e5 > 3,
  );
  if (
    categoriesIds?.length &&
    categoriesIds
      .filter((id): id is string => id !== undefined)
      .some((categoryId) =>
        LAST_CATEGORY_ID_FOR_MIGRATION.includes(
          categoryId.slice(categoryId.length - 5),
        ),
      ) &&
    !isAnyCategoryChanged
  ) {
    isMigratingCategory = true;
  }
  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setFirstQuestionId?.(undefined);
    const category = categoriesToRender[index];
    logger.report(
      faqButtonClickSrc63Evid836({
        button_name: category?.id || category?.title || `${index}`,
        action: 'tab',
      }),
    );
  };

  const isDisplayCategories =
    (!searchTerm || searchTerm === '') &&
    splitIntoCategories &&
    categoriesToRender.length > 0 &&
    !isMigratingCategory;

  if (fetchError) {
    return null;
  }
  let categoriesWithLink = categoriesToRender;
  if (seo) {
    categoriesWithLink = categoriesToRender.map((category) => {
      const categoryURL = new URL(baseURL);
      categoryURL.searchParams.set('categoryId', `${category.id}`);
      categoryURL.searchParams.set('appDefId', appDefinitionId);
      return {
        ...category,
        href: categoryURL,
      };
    });
  }
  const categoriesList = categoriesWithLink.map((category, index) => {
    const { id, title, updatedDate, createdDate } = category;
    return {
      ...category,
      title: getCategoryTitleForOOI(
        id || '',
        index,
        title || '',
        t,
        updatedDate,
        createdDate,
      ),
    };
  });

  return (
    <>
      {isDisplayCategories && topCategoriesPosition && (
        <HorizontalCategories
          categoriesToRender={categoriesList as TabItem[]}
          adjustedActiveTab={activeTab}
          setActiveTab={handleTabClick}
          splitIntoCategories={!!splitIntoCategories}
          rtl={rtl || false}
        />
      )}
      {isDisplayCategories && sideCategoriesPosition && (
        <VerticalTabs
          sideCategoriesPosition={sideCategoriesPosition}
          categoriesToRender={categoriesList as TabItem[]}
          activeTab={activeTab}
          setActiveTab={handleTabClick}
        />
      )}
    </>
  );
};

export default TabsWrapper;
